<template>
  <div
    v-if="subsidyPrograms"
    class="h-100pc"
  >
    <v-row
      v-if="providers"
      class="h-100pc"
      dense
      no-gutters
    >
      <v-col class="h-100pc">
        <!-- TODO: These selectors need to be removed from component level and made global -->
        <v-row
          v-if="subsidyPrograms.length > 1"
          id="provider-index-subsidy-row"
          :class="{
            'bg-primary pt-3 mb-2': !store.state.site.enable_provider_v2,
            'bg-white bb-1 bc-very-light-grey': store.state.site.enable_provider_v2,
          }"
        >
          <v-col
            :class="{
              'd-flex justify-center py-6': !store.state.site.enable_provider_v2,
              'ms-5 pb-5 pt-0': store.state.site.enable_provider_v2,
            }"
          >
            <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
            <v-select
              v-model="selectedSubsidyProgram"
              :aria-label="$bct('Select a subsidy program')"
              :density="store.state.site.enable_provider_v2 ? 'compact' : 'comfortable'"
              :item-title="(sp) => sp.name"
              :items="subsidyPrograms"
              class="mxw-400"
              item-value="id"
              variant="solo"
              hide-details
              return-object
            />
          </v-col>
        </v-row>

        <v-container fluid>
          <v-row>
            <v-col>
              <v-card-title class="d-flex justify-space-between align-center">
                <span class="fs-28">{{ $bct('Locations') }}</span>

                <v-spacer />

                <StandardButton
                  v-if="
                    $store.state.pages.Provider.features.enable_add_location && providers.length > 1
                  "
                  @click="$refs.requestProviderLocationDialog.open()"
                  data-cy="add-location-top"
                  role="create"
                  text="Add location"
                />
              </v-card-title>
            </v-col>
          </v-row>

          <AccessRequestCard
            v-for="accessRequest in accessRequests"
            @destroyed="loadAccessRequests"
            :key="accessRequest.id"
            :request="accessRequest"
            class="mb-6"
          />

          <OwnershipRequestCard
            v-for="ownerRequest in ownershipRequests"
            @destroyed="loadOwnershipRequests"
            :key="ownerRequest.id"
            :request="ownerRequest"
            class="mb-6"
          />

          <v-row
            v-if="providers.length == 0 && ownershipRequests.length == 0"
            class="mb-4"
          >
            <v-col>
              <div
                id="no_provider_message"
                class="c-light-black ta-center bc-body"
                data-cy="no-provider-message"
              >
                {{
                  $bct("Oh no! Your email isn't associated with an existing child care program.")
                }}
              </div>
            </v-col>
          </v-row>

          <ProviderSummaryCard
            v-for="provider in providers"
            :key="provider.id"
            :provider="provider"
            :seat-types="seatTypes"
            :subsidy-program="selectedSubsidyProgram"
          />

          <div
            v-if="$store.state.pages.Provider.features.enable_add_location"
            class="mxw-300 mx-auto"
            data-cy="add-location-bottom"
          >
            <StandardButton
              @click="$refs.requestProviderLocationDialog.open()"
              role="create"
              size="x-large"
              text="Add location"
              full-width
            />
          </div>

          <VerticalSpacer :min-height="60" />
        </v-container>
      </v-col>
    </v-row>

    <RequestProviderLocationDialog
      @access-request-added="accessRequests.push($event)"
      @ownership-request-added="ownershipRequests.push($event)"
      ref="requestProviderLocationDialog"
      :access-requests="accessRequests"
      :ownership-requests="ownershipRequests"
    />
  </div>
</template>

<script setup>
import { useStore } from 'vuex';

import AccessRequestCard from '@/manager/components/provider/AccessRequestCard.vue';
import OwnershipRequestCard from '@/manager/components/provider/OwnershipRequestCard.vue';
import ProviderSummaryCard from '@/manager/components/provider/ProviderSummaryCard.vue';
import RequestProviderLocationDialog from '@/manager/components/provider/RequestProviderLocationDialog.vue';
import useApi from '@/shared/composables/useApi';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';

const $bct = inject('translator');

const { api: Api } = useApi();
const store = useStore();
const accessRequests = ref([]);
const ownershipRequests = ref([]);
const providers = ref(null);
const seatTypes = ref([]);
const selectedSubsidyProgram = ref(null);
const subsidyPrograms = ref(null);

watch(selectedSubsidyProgram, async () => {
  store.commit('setFilters', ['subsidy', { subsidyProgramId: selectedSubsidyProgram.value?.id }]);
  await loadSeatTypes();
});

onMounted(load);

async function load() {
  const response = await Api.shared.subsidyProgram.index();
  subsidyPrograms.value = response.data;

  const storedSubsidyProgramFilter = store.state.filters.subsidy?.subsidyProgramId;
  if (storedSubsidyProgramFilter) {
    selectedSubsidyProgram.value = subsidyPrograms.value.find(
      (program) => program.id === storedSubsidyProgramFilter,
    );
  } else {
    [selectedSubsidyProgram.value] = subsidyPrograms.value;
  }

  await Promise.allSettled([
    loadAccessRequests(),
    loadOwnershipRequests(),
    loadProviders(),
    loadSeatTypes(),
  ]);
}

async function loadAccessRequests() {
  const response = await Api.manager.accessRequest.index();
  accessRequests.value = response.data;
}

async function loadOwnershipRequests() {
  const response = await Api.manager.ownershipRequest.index({});
  ownershipRequests.value = response.data;
}

async function loadProviders() {
  const response = await Api.manager.provider.index();
  providers.value = response.data;
}

async function loadSeatTypes() {
  if (!selectedSubsidyProgram.value) return;

  seatTypes.value = [];
  const params = {
    providers_can_manage: true,
    providers_can_view: true,
  };
  const response = await Api.member.subsidy_program.seat_type.index(
    selectedSubsidyProgram.value?.id,
    params,
  );
  seatTypes.value = response.data;
}
</script>

<style scoped>
#provider-index-subsidy-row {
  position: sticky;
  top: var(--v-layout-top, 64px);
  z-index: 4;
}
</style>
